<script setup lang="ts">
import type { TabControlItem } from "@/types"
import { TabsContent, TabsIndicator, TabsList, TabsRoot, TabsTrigger } from "reka-ui"
import Icon from "@/components/ui/Icon/Icon.vue"

const props = defineProps<{
  tabs: TabControlItem[]
  tablistAriaLabel: string
  dataTest?: string
  tabDataTestPrefix?: string
}>()

const emit = defineEmits<{
  (e: "tabClick", id: string): void
}>()

const modelValue = defineModel<string>({ required: true })

const tabTriggerClass
  = "min-w-28 flex-1 basis-0 cursor-pointer border-x-0 border-t-0 border-b-4 border-transparent bg-transparent px-3 py-2.5 text-center text-xs font-medium uppercase tracking-wider text-gray-500 transition-colors hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--template-primary-colour),black_25%)] focus-visible:ring-offset-2 data-[state=active]:text-primary md:min-w-0 md:flex-none md:basis-auto"

function tabButtonDataTest(id: string): string {
  if (props.tabDataTestPrefix)
    return `${props.tabDataTestPrefix}-${id}`
  return `tab-control-tab-${id}`
}

function onTriggerActivate(tabId: string) {
  modelValue.value = tabId
  emit("tabClick", tabId)
}
</script>

<template>
  <TabsRoot
    v-model="modelValue"
    :unmount-on-hide="false"
    class="min-w-0 w-full max-w-full"
  >
    <TabsList
      class="tab-control-scroll relative flex min-w-0 w-full max-w-full items-stretch justify-center gap-6 overflow-x-auto border-b md:justify-start"
      :aria-label="tablistAriaLabel"
      :data-test="dataTest"
    >
      <TabsIndicator
        class="pointer-events-none absolute bottom-0 left-0 h-1 rounded-full bg-primary transition-[transform,width] duration-200 ease-out will-change-[transform,width] translate-x-(--reka-tabs-indicator-position) w-(--reka-tabs-indicator-size)"
      />
      <TabsTrigger
        v-for="tab in tabs"
        :key="tab.id"
        :value="tab.id"
        :class="tabTriggerClass"
        :data-test="tabButtonDataTest(tab.id)"
        @click="onTriggerActivate(tab.id)"
      >
        <span class="inline-flex items-center justify-center gap-1.5">
          <Icon
            v-if="tab.icon"
            :icon="tab.icon"
            class="size-3.5 shrink-0"
          />
          {{ tab.label }}
        </span>
      </TabsTrigger>
    </TabsList>

    <TabsContent
      v-for="tab in tabs"
      :key="`tab-panel-${tab.id}`"
      :value="tab.id"
    />
  </TabsRoot>
</template>

<style scoped>
.tab-control-scroll {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.tab-control-scroll::-webkit-scrollbar {
  display: none;
}
</style>
